<template>
  <div class="modal-document-preview">
    <Dialog width="895px" :canCloseManually="false" :showCloseIcon="true">
      <template slot="title">
        <h1 class="title">{{ modalDocumentPreview.title }}</h1>
      </template>
      <template slot="body">
        <div class="container">
          <div class="detail-preview-bar">
            <div class="detail-actions-left">
              <Button height="36" @click="downloadFile">
                <DownloadIcon /> Download
              </Button>
              <Button height="36" @click="print">
                <PrintIcon /> Print
              </Button>
              <Button v-if="hasPatientEmail" height="36" @click="sendToPatientEmail">
                <EmailIcon /> Send to Patient
              </Button>
              <div class="icon-delete-wrapper" @click="handleDeleteClick">
                <DeleteIcon class="icon-delete" />
              </div>
            </div>

            <div class="view-switcher" v-if="hasImportSession">
              <button
                :class="['view-switcher__btn', { 'is-active': viewMode === 'report' }]"
                @click="viewMode = 'report'"
              >
                Lab report
              </button>
              <button
                :class="['view-switcher__btn', { 'is-active': viewMode === 'source' }]"
                @click="handleSourceViewClick"
              >
                Source
              </button>
            </div>
          </div>

          <div class="preview-content-scroll">
            <BloodImportReportPreview
              v-if="hasImportSession && viewMode === 'report'"
              :patient="patient"
              :reportInfo="reportInfo"
              :markersData="stagedMarkersItems"
              :headers="headers"
              :expandedRows="expandedRows"
              @toggle-group="toggleRow"
            />
            <div v-if="viewMode === 'source' || !hasImportSession" class="pdf-viewer-container">
              <div v-if="fileUrl" class="pdf-canvas-render">
                <vue-pdf-embed
                  v-if="isPdf"
                  :key="fileUrl"
                  :source="fileUrl"
                  class="pdf-render-surface"
                />
                <img v-else :src="fileUrl" class="img-preview-modal" />
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading" class="loading d-flex justify-center px-2 py-2 align-center">
          <v-progress-circular
            indeterminate
            color="#008C89"
          ></v-progress-circular>
        </div>
      </template>
    </Dialog>

    <iframe id="pdfPrintFrame" style="display: none"></iframe>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import Dialog from '@/components/layouts/dialog'
import BloodImportReportPreview from '@/components/layouts/blood/BloodImportReportPreview'
import Button from '@/components/elements/Button'
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'
import { BLOOD_MARKERS_HEADERS } from '@/constants/tableHeaders'
import DownloadIcon from '@/assets/icons/components/download-green.svg'
import PrintIcon from '@/assets/icons/components/print-green.svg'
import EmailIcon from '@/assets/icons/components/email-green.svg'
import DeleteIcon from '@/assets/icons/components/delete-icon.svg'

export default {
  name: 'modal-document-preview',
  components: {
    VuePdfEmbed,
    Dialog,
    BloodImportReportPreview,
    Button,
    DownloadIcon,
    PrintIcon,
    EmailIcon,
    DeleteIcon
  },
  props: {
    loading: Boolean,
    hasPatientEmail: Boolean,
    patient: Object,
  },
  data: () => ({
    headers: BLOOD_MARKERS_HEADERS,
    expandedRows: [],
    viewMode: 'report'
  }),
  computed: {
    ...mapState('dialogs', ['modalDocumentPreview']),
    passedData() {
      return this.modalDocumentPreview.data || {}
    },
    hasImportSession() {
      return !!this.passedData.hasImportSession
    },
    importSession() {
      return this.passedData.importSession
    },
    stagedMarkersItems() {
      return this.passedData.stagedMarkersItems || []
    },
    fileUrl() {
      return this.passedData.fileUrl
    },
    isPdf() {
      const name = this.importSession?.fileName || this.modalDocumentPreview.title || ''
      return name.toLowerCase().endsWith('.pdf')
    },
    reportInfo() {
      const s = this.importSession
      if (!s) return {}

      const nameParts = [s.patientFirstName, s.patientLastName].filter(Boolean)
      const fullName = nameParts.length > 0 ? nameParts.join(' ') : '-'

      return {
        fileName: s.fileName,
        source: s.ImportSourceDataName || 'Doctor',
        patientName: fullName,
        patientDob: s.patientDateOfBirth ? this.$moment(s.patientDateOfBirth).format('MM/DD/YYYY') : '-',
        dateCollected: s.dateCollected ? this.$moment(s.dateCollected).format('MM/DD/YYYY') : '-',
        dateReceived: s.dateReceived ? this.$moment(s.dateReceived).format('MM/DD/YYYY') : '-',
        createdOn: s.createdOn ? this.$moment(s.createdOn).format('MM/DD/YYYY, hh:mm A') : '-',
        labName: s.labName || 'Unknown Lab',
        fasting: s.isFasting ? 'Yes' : 'No',
        valueCount: s.valueCount || 0
      }
    }
  },

  created() {
    this.viewMode = this.hasImportSession ? 'report' : 'source'
  },

  watch: {
    stagedMarkersItems: {
      immediate: true,
      handler(val) {
        if (val?.length) this.expandedRows = val.map(g => g.name)
      }
    }
  },

  methods: {
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),

    close() {
      this.TOGGLE_DIALOG({ name: 'modalDocumentPreview', isOpen: false, root: true })
    },

    downloadFile() {
      this.$emit('download')
    },

    print() {
      this.$emit('print')
    },

    sendToPatientEmail() {
      this.$emit('send-email')
    },

    handleDeleteClick() {
      this.$emit('delete')
    },

    handleSourceViewClick() {
      this.viewMode = 'source'
      if (!this.fileUrl) {
        this.$emit('load-source')
      }
    },

    toggleRow(name) {
      const index = this.expandedRows.indexOf(name)
      index > -1 ? this.expandedRows.splice(index, 1) : this.expandedRows.push(name)
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-document-preview {
  position: relative;

  ::v-deep .v-dialog {
    max-height: 880px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: 1496px) {
      max-height: 100vh;
    }

    .v-card {
      max-height: 880px;
      display: flex;
      flex-direction: column;


      background-color: #FFFFFF;
      overflow: hidden;

      &__title {
        flex: 0 0 auto;
        padding: 36px !important;

        .title {
          color: #27272E;
          font-size: 28px !important;
          font-weight: 600;
          line-height: 38px;
          letter-spacing: 0 !important;
        }

        .image {
          cursor: pointer;
        }
      }

      &__text {
        display: flex;
        flex-direction: column;

        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
      }

      .container {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        padding: 0 36px;
        overflow: hidden;

        .detail-preview-bar {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background-color: #EBF2FA;
          border-radius: 8px 8px 0 0;
          padding: 9px 14px 9px 17px;
        }

        .preview-content-scroll {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
          overflow: hidden;
        }

        .detail-actions-left {
          display: flex;
          align-items: center;
          column-gap: 10px;

          .icon-delete-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            cursor: pointer;

            .icon-delete {
              width: 16px;
              height: 16px;

              path {
                fill: #718096 !important;
              }
            }
          }
        }

        .button .v-btn {
          background-color: transparent !important;
          border: 1px solid #CBD5E0 !important;
          color: #16192C;
          font-size: 12px;
          font-weight: 600;
          line-height: 12px;
          letter-spacing: 0;
          border-radius: 8px;
          padding: 10px 20px;

          &:focus::before {
            opacity: 0 !important;
          }

          .v-btn__content {
            column-gap: 10px;
          }
        }

        .view-switcher {
          display: flex;
          background: #F7FAFC;
          border-radius: 8px;
          padding: 2px;

          &__btn {
            width: 88px;
            height: 30px;
            color: #425466;
            font-size: 12px;
            font-weight: 600;
            line-height: 12px;
            letter-spacing: 0;
            border: none;
            background: transparent;
            border-radius: 8px;
            padding: 9px 8px;
            cursor: pointer;
            transition: all 0.3s ease;

            &.is-active {
              background-color: #008C89;
              color: #FFFFFF;
            }
          }
        }
      }

      .table-wrapper {
        .table {
          .v-data-table {
            border-radius: 0;
          }
        }
      }
    }
  }

  .pdf-viewer-container {
    position: relative;
    width: 100%;
    height: 590px;
    background-color: #edf2f7;
    overflow: hidden;

    .pdf-canvas-render {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      box-sizing: border-box;
      overflow-y: auto;
      overflow-x: hidden;

      .pdf-render-surface {
        width: 100%;
        box-sizing: border-box;
        padding: 15px 15px 0;

        ::v-deep canvas {
          display: block;
          width: 100% !important;
          height: auto !important;
          max-width: 100% !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          margin: 0 auto 20px auto;
        }
      }
      &::-webkit-scrollbar {
        width: 8px;
      }
      &::-webkit-scrollbar-track {
        background: #f1f5f9;
      }
      &::-webkit-scrollbar-thumb {
        background: #cbd5e0;
        border-radius: 10px;
        border: 2px solid #f1f5f9;
      }
    }
  }

  .loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgb(213 213 213 / 50%);
    z-index: 999;
  }
}
</style>
